import { Group, delayedPromise, groupBy } from '@anecdotes/utils';
import {
  ChangeDetectorRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostBinding,
  HostListener,
  Injector,
  OnDestroy,
  OnInit,
  Output, inject, DestroyRef } from '@angular/core';
import markJS from 'mark.js';
import { Observable, Subject, filter, firstValueFrom, map, shareReplay, startWith, switchMap } from 'rxjs';
import { DataFilterManagerService, SearchInstancesManagerService } from '../../services';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

/** This trick with stylesheet is used so we can set CSS for highlights and control them from this directive */
const stylesheet = document.createElement('style');
stylesheet.innerHTML = `
.searchable-text .search-highlight {
  background-color: #dafdff;
}

.searchable-text .search-highlight.current-highlight {
  background-color: #99f8fd;
}
`;
document.head.appendChild(stylesheet);

@Directive({
  selector: '[searchableText]',
})
export class SearchableTextDirective implements OnInit, OnDestroy {
  private readonly destroyRef = inject(DestroyRef);
  private readonly contentChangeSubject = new Subject();
  private mutationObserver: MutationObserver;
  private markInstance;
  private searchTextChange$: Observable<string>;
  private textWithHighlights: string;

  @HostBinding('class.in-focus')
  private isInFocus: boolean;
  private currentScopeKey: string;

  @HostBinding('class')
  private readonly classes = 'searchable-text';

  @Output()
  focusLeave = new EventEmitter(true);

  @Output()
  focusIn = new EventEmitter(true);

  constructor(
    private cd: ChangeDetectorRef,
    private searchInstancesManagerService: SearchInstancesManagerService,
    private injector: Injector,
    private elementRef: ElementRef<HTMLElement>,
  ) {}

  ngOnInit(): void {
    this.mutationObserver = new MutationObserver(() => this.contentChangeSubject.next(undefined));
    this.markInstance = new markJS(this.elementRef.nativeElement);
    this.currentScopeKey = this.searchInstancesManagerService.getSearchScopeKey(this.elementRef.nativeElement);
    this.searchTextChange$ = this.getSearchText().pipe(
      map((searchValue) => searchValue || ''),
      shareReplay({ bufferSize: 1, refCount: true }),
    );
    this.searchTextChange$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((searchText) => this.updateHighlightsAsync(searchText));

    this.contentChangeSubject
      .pipe(
        filter(() => this.elementRef.nativeElement.textContent !== this.textWithHighlights),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(async () => {
        this.getHiglighlightableGroupedByParent()
          .map((group) => this.findAllNextElements(group.values[0]))
          .flat()
          .forEach((element) => element.remove());
        await this.updateHighlightsAsync(await firstValueFrom(this.searchTextChange$));
      });
  }

  ngOnDestroy(): void {
    this.detachContentChangeListener();
  }

  private detachContentChangeListener(): void {
    this.mutationObserver.disconnect();
  }

  private attachContentChangeListener(): void {
    this.mutationObserver.observe(this.elementRef.nativeElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });
  }

  private findAllNextElements(element: HTMLElement): HTMLElement[] {
    if (element.nextSibling) {
      return [element, ...this.findAllNextElements(element.nextSibling as HTMLElement)];
    }

    return [element];
  }

  private getSearchText(): Observable<string> {
    return this.searchInstancesManagerService.getSearchResultsPaginator(this.currentScopeKey).pipe(
      switchMap((searchResultsPaginator) => {
        if (searchResultsPaginator?.filterInstanceId) {
          const dataFilterManagerService: DataFilterManagerService = this.injector.get(DataFilterManagerService);
          return dataFilterManagerService
            .getSearchText(searchResultsPaginator.filterInstanceId)
            .pipe(map((searchText) => searchText || ''));
        } else {
          return this.searchInstancesManagerService.getDataSearch(this.currentScopeKey).pipe(
            filter((dataSearch) => !!dataSearch),
            switchMap((dataSearch) => dataSearch.searchField.valueChanges.pipe(startWith(dataSearch.inputtedText))),
          );
        }
      }),
    );
  }

  private async updateHighlightsAsync(searchText: string): Promise<void> {
    this.detachContentChangeListener();
    await this.unMarkAsync();
    await this.markAsync(searchText);
    this.setHighlightIds(searchText);
    await this.unMarkHighlightsWithoutIdAsync();
    await delayedPromise(100);
    this.textWithHighlights = this.elementRef.nativeElement.textContent;
    this.attachContentChangeListener();
  }

  private async markAsync(searchText: string): Promise<void> {
    await new Promise<void>((resolve) => {
      this.markInstance.mark(searchText, {
        element: 'span',
        className: 'search-highlight',
        acrossElements: true,
        done: resolve,
      });
    });
  }

  private getHiglighlightableGroupedByParent(): Group<HTMLElement, HTMLElement>[] {
    return groupBy(
      Array.from(this.elementRef.nativeElement.querySelectorAll('.search-highlight')),
      (element) => element.parentElement as HTMLElement,
    );
  }

  private unMarkAsync(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.markInstance.unmark({
        element: 'span',
        className: 'search-highlight',
        done: resolve,
      });
    });
  }

  private unMarkHighlightsWithoutIdAsync(): Promise<void> {
    return new Promise<void>((resolve) => {
      this.markInstance.unmark({
        element: 'span',
        className: 'search-highlight',
        exclude: ['[highlight-index]'],
        done: resolve,
      });
    });
  }

  private setHighlightIds(searchText: string): void {
    const highlightNodes = this.elementRef.nativeElement.querySelectorAll('span.search-highlight');
    let terms: string[] = [];
    let nodes: HTMLElement[] = [];
    let highlightIndex = 0;

    highlightNodes.forEach((node: HTMLElement) => {
      terms.push(node.textContent.toLowerCase());
      nodes.push(node);

      if (terms.join('').length > searchText.length) {
        terms = [];
        nodes = [];
      }

      if (
        terms.join('').toLowerCase() === searchText.toLowerCase() ||
        (nodes.every((node) => node.parentNode === nodes[0].parentNode) &&
          terms.join(' ').toLowerCase() === searchText.toLowerCase())
      ) {
        nodes.forEach((node) => {
          node.setAttribute('highlight-index', highlightIndex.toString());
        });
        nodes = [];
        terms = [];
        highlightIndex++;
      }
    });
  }

  @HostListener('focusTerm')
  private onFocus(): void {
    this.isInFocus = true;
    this.cd.markForCheck();
    this.cd.detectChanges();
    this.focusIn.emit();
  }

  @HostListener('focusTermRemoved')
  private onFocusLeave(): void {
    delete this.isInFocus;
    this.cd.markForCheck();
    this.cd.detectChanges();
    this.focusLeave.emit();
  }
}
